import type { No } from '@mymaxx/flow-engine';
import { Handle, Position, type NodeProps, type Node } from '@xyflow/react';
import { CATALOGO_NOS, resumoDoNo, saidasDoNo } from './catalogo-nos';

export type DadosNo = {
  no: No;
  /** Destacado durante a simulação (é onde a execução está parada). */
  emExecucao?: boolean;
  /** O grafo tem um problema apontando para este nó. */
  comProblema?: boolean;
};

export type NoCanvas = Node<DadosNo, 'mymaxx'>;

/**
 * Um nó do fluxo desenhado no canvas. As saídas viram "handles" à direita:
 * saída única fica no meio, e nós com caminhos (menu, condição) ganham um por caminho,
 * com o nome do lado para o admin saber o que está ligando.
 */
export function NoFluxo({ data, selected }: NodeProps<NoCanvas>) {
  const { no, emExecucao, comProblema } = data;
  const descricao = CATALOGO_NOS[no.tipo];
  const saidas = saidasDoNo(no);
  const { Icone } = descricao;

  return (
    <div
      className={`w-60 rounded-lg border-2 bg-white text-left shadow-sm transition dark:bg-neutral-900 ${
        emExecucao
          ? 'border-amber-500 ring-4 ring-amber-200 dark:ring-amber-900'
          : comProblema
            ? 'border-red-500'
            : selected
              ? 'border-marca-500'
              : 'border-neutral-200 dark:border-neutral-700'
      }`}
    >
      {descricao.temEntrada && (
        <Handle type="target" position={Position.Top} className="!size-3 !bg-neutral-400" />
      )}

      <header
        className="flex items-center gap-2 rounded-t-md px-3 py-2 text-xs font-medium text-white"
        style={{ backgroundColor: descricao.cor }}
      >
        <Icone size={14} />
        {descricao.nome}
      </header>

      <div className="px-3 py-2">
        <p className="line-clamp-3 text-xs break-words text-neutral-700 dark:text-neutral-300">
          {resumoDoNo(no)}
        </p>
      </div>

      {saidas.length === 0 && descricao.saidas.length === 0 && no.tipo !== 'menu' && (
        <p className="px-3 pb-2 text-[10px] text-neutral-400">Este nó encerra o caminho.</p>
      )}

      {/* Saída única: um ponto embaixo, sem rótulo. */}
      {saidas.length === 1 && saidas[0]?.id === undefined && (
        <Handle type="source" position={Position.Bottom} className="!size-3 !bg-neutral-400" />
      )}

      {/* Vários caminhos: um ponto por saída, com o nome ao lado. */}
      {saidas.some((s) => s.id !== undefined) && (
        <ul className="border-t border-neutral-200 dark:border-neutral-700">
          {saidas.map((saida, i) => (
            <li
              key={saida.id ?? i}
              className="relative flex items-center justify-end gap-2 px-3 py-1.5 text-[11px] text-neutral-600 dark:text-neutral-400"
            >
              {saida.nome}
              <Handle
                id={saida.id}
                type="source"
                position={Position.Right}
                className="!size-3"
                style={{ backgroundColor: saida.cor ?? '#9ca3af', top: 'auto' }}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
